import { capitalize, Skeleton } from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import DrawerTitle from "src/Components/Drawer/DrawerTitle";
import {
  AllTabType,
  IHomeCareProductDocument,
} from "src/types/GlobalInterfaces";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import BloodIcon from "src/assets/images/website-images/blood-icon.png";
import {
  faChevronLeft,
  faChevronRight,
  faCreditCard,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import {
  appointmentSlots,
  CURRENCY,
  ImageBaseUrl,
  IDynamicSlot,
} from "src/lib/constans";
import GradientBtn from "src/Components/WebsiteComponents/GradientBtn";
import CalendarIcon from "src/assets/images/website-images/calendar.png";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import ManageAddressScreen from "../ManageAddressScreen";
import DileveryAddressTitle from "src/Components/GlobalComponents/DileveryAddressTitle";
import { useSelector } from "react-redux";
import { RootState } from "src/Store/store";
import { toast } from "react-toastify";
import httpRequest from "src/helpers/httpRequest";
import { useNavigate } from "react-router-dom";
import EditProfileScreen from "../EditProfileScreen";

interface IProps {
  data: IHomeCareProductDocument;
  selectedDate: string;
  selectedSlot: IDynamicSlot | null;
  onBack: () => void;
  onSuccess: () => void;
}

const stripePromise = loadStripe(process.env.REACT_APP_VITE_STRIPE_PK || "");

interface IOrderProps {
  data: IHomeCareProductDocument;
  selectedDate: string;
  selectedSlot: IDynamicSlot | null;
  setAddreModal: (value: boolean) => void;
  onSuccess: () => void;
  setShowEditProfile: (value: boolean) => void;
}

const OrderForm: React.FC<IOrderProps> = ({
  data,
  selectedDate,
  selectedSlot,
  setAddreModal,
  onSuccess,
  setShowEditProfile,
}) => {
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const { address } = useSelector((state: RootState) => state.cartSlice);
  const { userDetails } = useSelector((state: RootState) => state.appReducer);
  const stripe = useStripe();
  const elements = useElements();

  //   handle confirm booking
  const handleConfirmBooking = async () => {
    if (!address || typeof address === "string") {
      setAddreModal(true);
      return;
    }

    if (!userDetails?.gender || !userDetails?.dob) {
      setShowEditProfile(true);
      toast("Please update your profile to continue", { type: "error" });
      return;
    }

    if (!stripe || !elements) {
      toast.error("Stripe or Elements not loaded");
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    const cardExpiryElement = elements.getElement(CardExpiryElement);
    const cardCvcElement = elements.getElement(CardCvcElement);
    if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
      console.error("Card Element(s) not found");
      return;
    }
    // Validate card details
    setIsLoader(true);
    // Create payment method
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardNumberElement, // Use cardNumberElement for full validation
    });

    if (error) {
      toast(error.message, { type: "error" });
      console.log("Validation error:", error.message);
      setIsLoader(false);
      return;
    }
    // Handle payment
    try {
      const paymentIntentResponse = await httpRequest<{
        clientSecret: string;
        paymentIntentId: string;
      }>({
        method: "post",
        path: "/stripe/create-homecare-payment-intent",
        params: {
          currency: CURRENCY,
          amount: data?.price,
          serviceId: data?._id,
        },
      });
      if (paymentIntentResponse.err) {
        toast(paymentIntentResponse.err, { type: "error" });
        throw new Error(paymentIntentResponse.err);
      }
      const paymentIntentId: string =
        paymentIntentResponse?.res?.paymentIntentId || "";
      const clientSecret: string =
        paymentIntentResponse?.res?.clientSecret || "";
      const { paymentIntent, error: confirmError } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethod.id,
        });
      if (confirmError) {
        toast(confirmError.message, { type: "error" });
        setIsLoader(false);
        return;
      }
      if (paymentIntent?.status === "succeeded") {
        const obj = {
          shippingAddress: address?.streetAddress || "",
          time: selectedSlot?.time || "",
          appointmentDate: selectedDate,
          date: userDetails?.dob || "",
          patientName: address?.personName || "Not Provided",
          service: { _id: data?._id, price: data?.price },
          phoneNumber: address?.mobileNumber || "",
          customerId: userDetails?._id || "",
          daySlot: selectedSlot?.period || "",
          totalAmount: data?.price,
          gender: userDetails?.gender || "",
          paymentIntentId,
        };
        const formData = new FormData();
        formData.append("createHomeCareServiceOrderDto ", JSON.stringify(obj));
        const payStatus = await httpRequest<{
          creditPoints: number;
          aedValue: number;
        }>({
          method: "post",
          path: "/home-care-order/create-order",
          params: formData,
          header: { "Content-Type": "multipart/form-data" },
        });
        if (payStatus.err) {
          toast(payStatus.err, { type: "error" });
          throw new Error(payStatus.err);
        } else {
          onSuccess();
        }
      } else {
        toast("Payment failed. Please try again.", { type: "error" });
      }
    } catch (err) {
      console.error("Error during API calls:", err);
      toast("An error occurred during processing. Please try again.", {
        type: "error",
      });
    } finally {
      setIsLoader(false);
    }
  };
  return (
    <form
      onSubmit={handleConfirmBooking}
      id="checkout-form-of-cart"
      className="box-design w-full p-3"
    >
      <p className="font-Poppins-Medium font-size-18px text-[#2F2F31] pb-2">
        Payment Method
      </p>
      <div className="w-full rounded-lg border border-[#22C55E] mb-2 flex px-3 py-2 items-center gap-2 bg-[#F0FDF4]">
        <FontAwesomeIcon icon={faCreditCard} size="lg" />
        <p className="text-[#2F2F31] font-Poppins-Regular font-size-15px">
          Card
        </p>
      </div>
      <div className="grid grid-cols-1 gap-2">
        <div>
          <div className="visa-card-number relative">
            <div className="card-icon-checkout">
              <FontAwesomeIcon icon={faCreditCard} size="lg" />
            </div>
            <CardNumberElement
              className="stripe-des font-size-16px font-Poppins-Regular paymnet-form-input pl-[calc(32px+0.6rem)]"
              id="cardNumber"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <CardExpiryElement
              className="stripe-des font-size-16px font-Poppins-Regular paymnet-form-input"
              id="CardExpiryElement"
            />
          </div>
          <div>
            <CardCvcElement
              className="stripe-des font-size-16px font-Poppins-Regular paymnet-form-input"
              id="CardCvcElement"
            />
          </div>
        </div>
      </div>
      <div className="w-full px-3 py-2 absolute items-center bottom-0 left-0 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.1)] flex gap-3 mt-5">
        <div className="flex flex-col">
          <p className="font-Poppins-SemiBold text-[#2F2F31] font-size-18px">
            Fee: {CURRENCY}{" "}
            {parseFloat(data?.price?.toString() || "0")?.toFixed(2)}
          </p>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <GradientBtn
            ButtonText="Continue"
            onPress={handleConfirmBooking}
            loader={isLoader}
            btnExtraStyle={{
              width: "100%",
              transition: "all 0.3s",
              opacity: selectedSlot && selectedDate && data?.price ? 1 : 0.5,
              cursor:
                selectedSlot && selectedDate && data?.price
                  ? "pointer"
                  : "not-allowed",
            }}
          />
        </div>
      </div>
    </form>
  );
};

const BookHomeCareSection = ({
  data,
  selectedDate,
  selectedSlot,
  onBack,
  onSuccess,
}: IProps) => {
  const [addreModal, setAddreModal] = useState<boolean>(false);
  const [showEditProfile, setShowEditProfile] = useState<boolean>(false);
  return (
    <>
      <div className={`${addreModal || showEditProfile ? "hidden" : "block"}`}>
        <DrawerTitle
          title="Confirm Booking"
          backBtn
          onBack={() => {
            onBack();
          }}
        />
        <div className="2xl:p-4 p-2 gap-4 flex flex-col">
          <DileveryAddressTitle
            name="Address"
            onAddressClick={() => setAddreModal(true)}
          />
          <div className="w-full flex flex-col box-design overflow-hidden">
            <div className="w-full flex gap-2 p-2 rounded-[10px] shadow-[0px_0px_2px_#00000029]">
              <div
                className={`w-[100px] aspect-square rounded-[5px] overflow-hidden ${
                  data?.image ? "" : "bg-image-loader"
                }`}
              >
                {data?.image && (
                  <img
                    src={data?.image}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="flex flex-col">
                {data?.categoryName && (
                  <p className="font-Poppins-Regular mt-[-2px] font-size-15px text-[#004540]">
                    {data?.categoryName}
                  </p>
                )}
                {data?.serviceName && (
                  <p className="font-Poppins-Medium mt-[-2px] font-size-18px text-[#272727]">
                    {data?.serviceName}
                  </p>
                )}
                <div className="flex gap-1 items-center">
                  <div className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <FontAwesomeIcon
                        icon={faStar}
                        className={`${
                          data?.averageRating > index
                            ? "text-[#facc15] text-[15px] 2xl:text-[17px]"
                            : "text-[#b7b6b1] text-[15px] 2xl:text-[17px]"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="font-Poppins-Regular font-size-16px mb-[-2px] text-[#707070]">
                    {parseFloat(
                      data?.averageRating?.toString() || "0"
                    )?.toFixed(1)}
                    {" - "}
                    {data?.totalReviews || 0}{" "}
                    {data?.totalReviews && data?.totalReviews > 1
                      ? "Reviews"
                      : "Review"}
                  </p>
                </div>
                <div className="flex gap-1 items-center mt-1">
                  {/* <div className="w-[25px] aspect-square border border-[#4F6367] bg-[#FFE175] rounded-[5px] flex items-center justify-center">
                <img
                  src={BloodIcon}
                  alt=""
                  className="w-[80%] h-[80%] object-contain"
                />
              </div> */}
                  <p className="font-Poppins-Regular font-size-15px text-[#707070]">
                    {data?.healthCareProvider}
                  </p>
                </div>
              </div>
            </div>
            <div className="w-full flex flex-col p-2 gap-2">
              <p className="font-Poppins-Medium font-size-18px text-[#2F2F31]">
                Date & Time
              </p>
              <div className="w-full flex gap-7 items-center">
                <div className="flex gap-2 items-center">
                  <img
                    src={CalendarIcon}
                    alt=""
                    className="w-[18px] object-contain"
                  />
                  <p className="font-Poppins-Regular font-size-15px text-[#5B5B5B]">
                    {moment(selectedDate).format("DD MMM")}
                  </p>
                </div>
                <div className="flex gap-2 items-center">
                  <img
                    src={selectedSlot?.image}
                    alt=""
                    className="w-[18px] object-contain"
                  />
                  <p className="font-Poppins-Regular font-size-15px text-[#5B5B5B]">
                    {selectedSlot?.time}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Checkout Form */}
          <Elements stripe={stripePromise}>
            <OrderForm
              data={data}
              selectedDate={selectedDate}
              selectedSlot={selectedSlot}
              setAddreModal={setAddreModal}
              onSuccess={() => {
                onSuccess?.();
              }}
              setShowEditProfile={setShowEditProfile}
            />
          </Elements>
        </div>
      </div>
      {showEditProfile && (
        <EditProfileScreen
          onBack={() => setShowEditProfile(false)}
          setActiveTab={(tab: AllTabType) => {
            setShowEditProfile(false);
          }}
        />
      )}
      {addreModal && (
        <ManageAddressScreen onBack={() => setAddreModal(false)} directBack />
      )}
    </>
  );
};

export default BookHomeCareSection;
